import React, { useState, useMemo, useEffect } from 'react';
import { Heart, HeartHandshake, Infinity as InfinityIcon, ShieldCheck, Lock, Check, ArrowRight, ArrowLeft, CheckCircle2, CreditCard, Upload, Pencil, User } from 'lucide-react';
import { FORM_STEPS, FORM_SCHEMA, SECTION_TITLES, PLANS } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { API, authHeaders, getToken, fmtErr, mediaUrl } from '../lib/auth';
import axios from 'axios';
import { Link } from 'react-router-dom';

const PLAN_ICONS = { Heart, HeartHandshake, Infinity: InfinityIcon };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const REVIEW_STEP = FORM_SCHEMA.length; // step index 3

const buildInitial = () => {
  const init = { email: '', phone: '', instagram: '', channel: '' };
  FORM_SCHEMA.forEach((s) => s.fields.forEach((f) => {
    if (f.type === 'checkbox') init[f.name] = f.default ? [...f.default] : [];
    else init[f.name] = f.default || '';
  }));
  return init;
};

const CHANNEL_INPUTS = {
  WhatsApp: { name: 'phone', label: 'Contact / WhatsApp Number', type: 'tel', placeholder: '+91 Enter your number', hint: 'Just for confirmation — or so we can call you for the refund process if no match is found.' },
  Email: { name: 'email', label: 'Email Address', type: 'email', placeholder: 'Enter your email' },
  Instagram: { name: 'instagram', label: 'Instagram ID', type: 'text', placeholder: '@username' },
};

const MatchmakingSection = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState('199');
  const [form, setForm] = useState(buildInitial);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paid, setPaid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [agree, setAgree] = useState(false);
  const [result, setResult] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [loggedInPhoto, setLoggedInPhoto] = useState(null);

  useEffect(() => {
    if (!getToken()) return;
    axios.get(`${API}/user/me`, { headers: authHeaders() }).then(({ data }) => setLoggedInPhoto(data.photos?.[0]?.url || null)).catch(() => {});
  }, []);

  const photoPreviewUrl = useMemo(() => (photoFile ? URL.createObjectURL(photoFile) : null), [photoFile]);
  useEffect(() => () => { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); }, [photoPreviewUrl]);

  const selectedPlan = PLANS.find((p) => p.id === plan);
  const refundExample = useMemo(() => (parseInt(plan, 10) * 0.9).toFixed(2), [plan]);

  const setVal = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const toggleArr = (name, opt) => setForm((prev) => {
    const arr = prev[name] || [];
    return { ...prev, [name]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
  });

  const scrollTop = () => document.getElementById('match')?.scrollIntoView({ behavior: 'smooth' });

  const validateStep = (s) => {
    for (const f of FORM_SCHEMA[s].fields) {
      if (!f.required) continue;
      const v = form[f.name];
      const empty = Array.isArray(v) ? v.length === 0 : !String(v || '').trim();
      if (empty) {
        toast({ title: `${f.label} is required`, description: 'Please fill this field to continue.' });
        return false;
      }
    }
    if (s === 0 && form.age && (isNaN(parseInt(form.age, 10)) || parseInt(form.age, 10) < 18 || parseInt(form.age, 10) > 99)) {
      toast({ title: 'Please check your age', description: 'Age must be between 18 and 99.' });
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, REVIEW_STEP));
    scrollTop();
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); scrollTop(); };
  const goEdit = (i) => { setStep(i); scrollTop(); };

  const submitForm = (e) => {
    e.preventDefault();
    if (!form.channel) {
      toast({ title: 'Contact preference required', description: 'Please select how you would like us to contact you.' });
      return;
    }
    const ci = CHANNEL_INPUTS[form.channel];
    const cv = (form[ci.name] || '').trim();
    if (!cv) {
      toast({ title: `${ci.label} is required`, description: `Please enter your ${ci.label.toLowerCase()}.` });
      return;
    }
    if (form.channel === 'Email' && !EMAIL_RE.test(cv)) {
      toast({ title: 'Valid email required', description: 'Please enter a valid email address.' });
      return;
    }
    if (form.channel === 'WhatsApp' && cv.replace(/\D/g, '').length < 10) {
      toast({ title: 'Valid contact number required', description: 'Please enter a valid WhatsApp/mobile number.' });
      return;
    }
    if (form.email && !EMAIL_RE.test(form.email.trim())) {
      toast({ title: 'Valid email required', description: 'Please enter a valid email address.' });
      return;
    }
    if (!agree) {
      toast({ title: 'Please accept the terms', description: 'Tick the terms & privacy checkbox to continue.' });
      return;
    }
    setPaid(false);
    setCheckoutOpen(true);
  };

  const confirmPay = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/submissions`, {
        data: form,
        plan_id: plan,
        plan_name: selectedPlan?.name,
        plan_price: `${selectedPlan?.price}${selectedPlan?.per ? ` ${selectedPlan.per}` : ''}`,
        payment_status: 'Paid (Demo)',
      }, { headers: authHeaders() });
      setResult(data);
      setPaid(true);
    } catch (err) {
      toast({ title: 'Submission failed', description: fmtErr(err.response?.data?.detail) });
    }
    setProcessing(false);
  };

  const closeCheckout = () => {
    setCheckoutOpen(false);
    setTimeout(() => setPaid(false), 300);
  };

  const renderField = (f) => {
    const cls = `field${f.full ? ' full' : ''}`;
    const otherOn = f.allowOther && (
      f.type === 'checkbox' ? (form[f.name] || []).some((o) => o === 'Other' || o === 'Others') : (form[f.name] === 'Other' || form[f.name] === 'Others')
    );
    return (
      <div className={cls} key={f.name}>
        <label>{f.label}{f.required && <span style={{ color: 'var(--rosepink)' }}> *</span>}</label>
        {(f.type === 'text' || f.type === 'number' || f.type === 'email' || f.type === 'tel') && (
          <input type={f.type} placeholder={f.placeholder} value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)} data-testid={`field-${f.name}`} />
        )}
        {f.type === 'textarea' && (
          <textarea placeholder={f.placeholder} value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)} data-testid={`field-${f.name}`} />
        )}
        {f.type === 'select' && (
          <select value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)} data-testid={`field-${f.name}`}>
            <option value="">Select</option>
            {f.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        )}
        {f.type === 'file' && (
          <label className="file-input">
            <Upload size={15} /> {form[f.name] ? form[f.name] : 'Choose a photo'}
            <input type="file" accept="image/*" hidden onChange={(e) => { const file = e.target.files?.[0]; setPhotoFile(file || null); setVal(f.name, file?.name || ''); }} data-testid={`field-${f.name}`} />
          </label>
        )}
        {f.type === 'radio' && (
          <div className="opt-row">
            {f.options.map((o) => (
              <button type="button" key={o} className={`opt ${form[f.name] === o ? 'on' : ''}`} onClick={() => setVal(f.name, o)}>
                <span className="dot" />{o}
              </button>
            ))}
          </div>
        )}
        {f.type === 'checkbox' && (
          <div className="opt-row">
            {f.options.map((o) => {
              const on = (form[f.name] || []).includes(o);
              return (
                <button type="button" key={o} className={`opt ${on ? 'on' : ''}`} onClick={() => toggleArr(f.name, o)}>
                  <span className="box">{on && <Check size={12} />}</span>{o}
                </button>
              );
            })}
          </div>
        )}
        {otherOn && (
          <input
            type="text" className="other-input" placeholder="Please specify"
            value={form[`${f.name}Other`] || ''} onChange={(e) => setVal(`${f.name}Other`, e.target.value)}
            data-testid={`field-${f.name}-other`}
          />
        )}
        {f.hint && <span className="field-hint">{f.hint}</span>}
      </div>
    );
  };

  const displayVal = (name) => {
    const v = form[name];
    let out = Array.isArray(v) ? (v.length ? v.join(', ') : '—') : (v || '—');
    if (form[`${name}Other`]) out += ` (${form[`${name}Other`]})`;
    return out;
  };

  return (
    <section className="match-section" id="match">
      <div className="container">
        <div className="form-layout">
          {/* FORM */}
          <div className="formbox">
            <div className="match-heading">
              <Heart className="h-icon" size={22} fill="#8E1638" /> Start Your Matchmaking Journey <Heart className="h-icon" size={22} fill="#8E1638" />
            </div>
            <p className="trust-line" data-testid="privacy-trust-line">
              <ShieldCheck size={13} /> Your privacy matters. Your details are handled securely and never shared publicly.
            </p>

            <div className="steps">
              {FORM_STEPS.map((s, i) => (
                <div key={s} className={`stepdot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} onClick={() => i < step && goEdit(i)} style={{ cursor: i < step ? 'pointer' : 'default' }}>
                  <span className="stepnum">{i < step ? <Check size={12} /> : i + 1}</span>{s}
                </div>
              ))}
            </div>

            <form onSubmit={submitForm}>
              {step < REVIEW_STEP && (
                <div className="fields">
                  {FORM_SCHEMA[step].fields.map(renderField)}
                </div>
              )}

              {step === REVIEW_STEP && (
                <>
                  <div className="review-summary photo-preview-card" style={{ marginBottom: 16 }} data-testid="form-photo-preview">
                    <h4>Your Matchmaking Profile</h4>
                    <div className="rp-photo-row">
                      {(photoPreviewUrl || loggedInPhoto) ? (
                        <img className="rp-photo" src={photoPreviewUrl || mediaUrl(loggedInPhoto, true)} alt="Your profile" data-testid="form-photo-preview-img" />
                      ) : (
                        <div className="rp-photo rp-photo-empty" data-testid="form-photo-preview-empty"><User size={26} /></div>
                      )}
                      <div>
                        <b>{form.name || 'Your Name'}</b>
                        <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                          {[form.age && `${form.age} yrs`, form.city, form.looking].filter(Boolean).join(' · ') || 'Complete the form to see your preview'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="review-summary" style={{ marginBottom: 16 }}>
                    <h4>How would you like us to contact you? <span style={{ color: 'var(--rosepink)' }}>*</span></h4>
                    <div className="opt-row" style={{ marginBottom: 4 }}>
                      {Object.keys(CHANNEL_INPUTS).map((o) => (
                        <button type="button" key={o} className={`opt ${form.channel === o ? 'on' : ''}`} onClick={() => setVal('channel', o)} data-testid={`channel-${o.toLowerCase()}`}>
                          <span className="dot" />{o === 'WhatsApp' ? 'Contact / WhatsApp Number' : o === 'Email' ? 'Email Address' : 'Instagram ID'}
                        </button>
                      ))}
                    </div>
                    {form.channel && (
                      <div className="field full" style={{ marginTop: 10 }}>
                        <label>{CHANNEL_INPUTS[form.channel].label}<span style={{ color: 'var(--rosepink)' }}> *</span></label>
                        <input
                          type={CHANNEL_INPUTS[form.channel].type}
                          placeholder={CHANNEL_INPUTS[form.channel].placeholder}
                          value={form[CHANNEL_INPUTS[form.channel].name]}
                          onChange={(e) => setVal(CHANNEL_INPUTS[form.channel].name, e.target.value)}
                          data-testid={`field-${CHANNEL_INPUTS[form.channel].name}`}
                        />
                        {CHANNEL_INPUTS[form.channel].hint && <span className="field-hint">{CHANNEL_INPUTS[form.channel].hint}</span>}
                      </div>
                    )}
                  </div>

                  {SECTION_TITLES.map((title, i) => (
                    <div className="review-summary" style={{ marginBottom: 14 }} key={title}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4>{title}</h4>
                        <button type="button" className="edit-btn" onClick={() => goEdit(i)} data-testid={`edit-section-${i}`}><Pencil size={12} /> Edit</button>
                      </div>
                      <div className="review-grid">
                        {FORM_SCHEMA[i].fields.filter((f) => f.type !== 'file').map((f) => (
                          <div className="review-item" key={f.name}>
                            <span className="rlabel">{f.label}</span>
                            <span className="rval">{displayVal(f.name)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="review-summary">
                    <h4>Selected Plan</h4>
                    <div className="review-item" style={{ borderBottom: 0, paddingBottom: 0 }}>
                      <span className="rval">{selectedPlan?.name} · {selectedPlan?.price}{selectedPlan?.per ? ` ${selectedPlan.per}` : ''}</span>
                    </div>
                  </div>

                  <div className="privacy-note"><Lock size={13} /> Your information is safe with us and will never be shared.</div>
                  <div className="agree-row">
                    <button type="button" data-testid="terms-agree-checkbox" className={`opt ${agree ? 'on' : ''}`} onClick={() => setAgree(!agree)}>
                      <span className="box">{agree && <Check size={12} />}</span> I agree to Krysha's terms and privacy policy
                    </button>
                  </div>
                </>
              )}

              <div className="step-actions">
                {step > 0 ? (
                  <button type="button" className="btn btn-ghost back-btn" onClick={back}><ArrowLeft size={16} /> Back</button>
                ) : <span />}
                {step < REVIEW_STEP ? (
                  <button type="button" className="btn btn-red" onClick={next} data-testid="form-next-btn">Next Step <ArrowRight size={17} /></button>
                ) : (
                  <button type="submit" className="btn btn-red" data-testid="form-submit-btn">Submit My Match Form <ArrowRight size={17} /></button>
                )}
              </div>
            </form>
          </div>

          {/* PLANS */}
          <div className="summary">
            <h3 className="plan-title"><Heart className="p-icon" size={18} fill="#8E1638" /> Choose Your Plan <Heart className="p-icon" size={18} fill="#8E1638" /></h3>
            <div className="plans">
              {PLANS.map((p) => {
                const Ic = PLAN_ICONS[p.icon];
                return (
                  <div key={p.id} className={`choice ${plan === p.id ? 'active' : ''}`} onClick={() => setPlan(p.id)} data-testid={`plan-${p.id}`}>
                    {p.popular && <div className="pop-tag">MOST POPULAR</div>}
                    <div className="c-name">{p.name}</div>
                    <div className="c-icon"><Ic size={26} /></div>
                    <div className="c-price">{p.price}{p.per && <span className="per"> {p.per}</span>}</div>
                    <div className="c-desc">{p.desc}</div>
                  </div>
                );
              })}
            </div>

            <div className="guarantee">
              <div className="shield-ic"><ShieldCheck size={30} /></div>
              <div style={{ flex: 1 }}>
                <h4>1 Month Match Guarantee</h4>
                <p>If we are unable to provide you a suitable match within 1 month, you are eligible for a refund.</p>
                <p><b>Note:</b> 10% service/processing charge will be deducted from the total amount.</p>
                <div className="fine">Example: {selectedPlan?.price} plan → Refund ₹{refundExample} · <Link to="/terms" data-testid="guarantee-terms-link" style={{ color: 'var(--red)', textDecoration: 'underline', fontWeight: 600 }}>Terms &amp; Refund Policy</Link></div>
              </div>
            </div>

            <button className="btn btn-red" style={{ width: '100%', marginTop: 18 }} onClick={() => { setStep(REVIEW_STEP); scrollTop(); }}>
              Go to Review &amp; Pay <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={checkoutOpen} onOpenChange={(o) => (o ? setCheckoutOpen(true) : closeCheckout())}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-0">
          <DialogTitle className="sr-only">Checkout</DialogTitle>
          {!paid ? (
            <div style={{ padding: 28 }}>
              <div style={{ textAlign: 'center', marginBottom: 18 }}>
                <div style={{ width: 54, height: 54, borderRadius: 16, background: 'var(--pink)', color: 'var(--red)', display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
                  <CreditCard size={26} />
                </div>
                <h3 style={{ font: '700 24px "Playfair Display"', margin: 0 }}>Confirm Your Plan</h3>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Review your order before payment.</p>
              </div>

              <div style={{ border: '1px solid var(--line)', borderRadius: 14, padding: 16, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--muted)' }}>Name</span><b>{form.name || '—'}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--muted)' }}>Plan</span><b>{selectedPlan?.name}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--line)', paddingTop: 10, marginTop: 4 }}>
                  <span style={{ color: 'var(--muted)', fontSize: 14 }}>Amount Payable</span>
                  <b style={{ font: '700 26px "Playfair Display"', color: 'var(--red)' }}>{selectedPlan?.price}{selectedPlan?.per ? ` ${selectedPlan.per}` : ''}</b>
                </div>
              </div>

              <div style={{ background: '#fff5f7', border: '1px solid #f3d3db', borderRadius: 12, padding: '10px 12px', fontSize: 11.5, color: '#83767a', display: 'flex', gap: 8, marginBottom: 16 }}>
                <Lock size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--red)' }} />
                Secure demo checkout. Live payment gateway (UPI / Cards / Netbanking) will be connected soon.
              </div>

              <button className="btn btn-red" style={{ width: '100%' }} onClick={confirmPay} disabled={processing} data-testid="pay-btn">
                {processing ? 'Processing…' : <>Pay {selectedPlan?.price} <ArrowRight size={17} /></>}
              </button>
            </div>
          ) : (
            <div style={{ padding: 34, textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#eafaf0', color: '#1f9d55', display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
                <CheckCircle2 size={38} />
              </div>
              <h3 style={{ font: '700 26px "Playfair Display"', margin: '0 0 8px' }} data-testid="submission-success">Your Krysha form has been submitted.</h3>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6, margin: '0 auto', maxWidth: 340 }}>
                Thank you, {form.name?.split(' ')[0] || 'friend'}! Your <b>{selectedPlan?.name}</b> plan is confirmed.
              </p>
              <div style={{ background: 'var(--pink)', borderRadius: 12, padding: '12px 14px', margin: '16px 0 0', fontSize: 12.5, textAlign: 'left' }}>
                <div><b>Submission ID:</b> <span data-testid="submission-id" style={{ fontFamily: 'IBM Plex Mono' }}>{result?.id?.slice(0, 8).toUpperCase()}</span></div>
                {result?.krysha_id && <div style={{ marginTop: 4 }}><b>Krysha ID:</b> {result.krysha_id}</div>}
                <div style={{ marginTop: 8, color: 'var(--muted)' }}>What happens next: our team reviews your answers and reaches out on your preferred channel within 24 hours.</div>
                {!getToken() && (
                  <div style={{ marginTop: 8 }}>
                    <Link to="/login" style={{ color: 'var(--red)', fontWeight: 700, textDecoration: 'underline' }} data-testid="create-account-link">Create a free Krysha account</Link> with the same email to track your match, get a Krysha ID and unlock chat.
                  </div>
                )}
              </div>
              <button className="btn btn-red" style={{ width: '100%', marginTop: 18 }} onClick={closeCheckout}>Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MatchmakingSection;
